/**
 * useLogout - Hook for handling user logout.
 *
 * Provides a function to log out the current user.
 * Clears authentication state from storage and React Query cache.
 */

import { useQueryClient } from '@tanstack/react-query';
import { authQueryKeys, clearAuth, getStoredToken } from '../utils/authStorage';

const API_BASE_URL = '/api';

/**
 * Hook for handling logout.
 *
 * @returns Object with:
 * - `logout`: Function to log out the current user
 */
export function useLogout() {
  const queryClient = useQueryClient();

  const logout = async () => {
    // Try to call the logout API for audit logging (ignore errors)
    const token = getStoredToken();
    if (token) {
      try {
        await fetch(`${API_BASE_URL}/auth/logout`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
      } catch {
        // Ignore errors - we still want to clear local state
      }
    }

    // Clear auth data from localStorage
    clearAuth();
    // Clear auth data from React Query cache
    queryClient.setQueryData(authQueryKeys.currentUser(), null);
    // Invalidate auth queries to ensure fresh state
    queryClient.invalidateQueries({ queryKey: authQueryKeys.currentUser() });
  };

  return { logout };
}
